// Colour rendering for the WM Transaction Mgmt Data Profile's four colour dropdowns (Delphi WMD0081).
//
// The Delphi screen put a read-only TEdit beside each colour dropdown and set its .Color to the picked
// colour (uWMD0081.PAS ColorSelection + the four *Change handlers). There is no spare control here, so
// the colour is shown on the dropdown itself instead: a swatch against every entry in the open list,
// and the picked colour filling the closed SelectBox's text area.
//
// Every colour is defined in colorSwatch.css; this file only decides which classes apply. The
// SelectBox receives its classes through DevExtreme's elementAttr option, which removes the previous
// custom class on each change, so nothing here reaches into the widget's DOM or sets inline styles.
import { COLOR_OPTIONS } from "./CodeLists";
import "./colorSwatch.css";

/** Code 'Z' is clWindow in the Delphi, i.e. "no colour"; every other code has a palette class. */
const NO_COLOR = "Z";

const COLOR_CODES = COLOR_OPTIONS.map((option) => option.id).filter((id) => id !== NO_COLOR);

const isColorCode = (code) => COLOR_CODES.includes(code);

/** The four coded colour columns. */
export const COLOR_FIELD_NAMES = [
    "TransTabPendingColor",
    "TransTabWarningColor",
    "ItemTabPendingColor",
    "ItemTabWarningColor",
];

export const isColorField = (name) => COLOR_FIELD_NAMES.includes(name);

/** The palette class for one code; colorSwatch.css declares one per colour. */
const paletteClass = (code) => `wm-color--${code}`;

/** A small square showing one palette entry; None gets an empty dashed square instead. */
function buildSwatch(code) {
    const swatch = document.createElement("span");
    swatch.className = isColorCode(code)
        ? `wm-color-swatch ${paletteClass(code)}`
        : "wm-color-swatch wm-color-swatch--none";

    return swatch;
}

/** Dropdown list entry: swatch, then the colour's name as the code list already spells it. */
function colorItemTemplate(data) {
    const row = document.createElement("div");
    row.className = "wm-color-item";

    row.appendChild(buildSwatch(data?.id));

    const label = document.createElement("span");
    label.append(data?.text ?? "");
    row.appendChild(label);

    return row;
}

/** Root classes for a SelectBox showing `code`: filled for a real colour, plain for None or empty. */
function selectClassFor(code) {
    return isColorCode(code)
        ? `wm-color-select wm-color-filled ${paletteClass(code)}`
        : "wm-color-select";
}

/**
 * Points one colour SelectBox at the classes for `code`. Only the class entry of elementAttr is
 * replaced; DevExtreme swaps its previous custom class out for the new one itself.
 *
 * Driven from the screen's formFields rather than from a subscription on the widget: the dynamic
 * form already routes the SelectBox's onValueChanged into formFields, and a load, reset or save
 * replaces the value there too, so an effect on those values covers every way the colour can change.
 */
export function applyColorFill(widget, code) {
    if (!widget || typeof widget.option !== "function") return;

    const current = widget.option("elementAttr") || {};
    widget.option("elementAttr", { ...current, class: selectClassFor(code) });
}

/**
 * Seeds one SelectBox as it is created: the swatch list, plus the fill for the value it already holds.
 *
 * The disabled case needs nothing here: colorSwatch.css drops the fill while the widget root carries
 * dx-state-disabled, so toggling Use Color greys the dropdown out without any repaint.
 */
export function decorateColorSelect(widget) {
    if (!widget || typeof widget.option !== "function") return;

    widget.option("itemTemplate", colorItemTemplate);
    applyColorFill(widget, widget.option("value"));
}
